import React, { useState } from "react";
import moment from "moment";
import {
  createPaymentIn,
  deleteExpense,
  deletePaymentIn,
  deletePaymentOut,
  deletePurchase,
  deletePurchaseOrder,
  deletePurchaseReturn,
  deleteSale,
  deleteSaleOrder,
  deleteSaleReturn,
  makeSettlement,
} from "../../services/services";
import { toastError, toastSuccess } from "../../utils";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { useEffect } from "react";
import { confirmDelete } from "../../components/confirmDelete";

const PartyCardDetail = ({
  ledgerEntry,
  isConvertedEdit,
  onClose,
  invoiceNumber,
  showActions = true,
  onSettlementSuccess,
  onDeleteSuccess,
}) => {
  const [entry, setEntry] = useState(ledgerEntry);
  const [isSettelement, setIsSettelement] = useState(false);
  const [settleAmount, setSettleAmount] = useState(entry?.dueAmount || 0);
  const navigate = useNavigate();

  useEffect(() => {
    setEntry(ledgerEntry);
    setSettleAmount(ledgerEntry?.dueAmount || 0);
  }, [ledgerEntry]);

  if (!entry) return null;
  const isPaymentEntry = ["payment-in", "payment-out", "expense"].includes(
    entry.referenceType,
  );
  const amountText = (() => {
    if (entry.referenceType === "payment-in") return "Amount Received";
    if (entry.referenceType === "payment-out") return "Amount Paid";
    if (entry.referenceType === "sale") return "Amount Received";
    if (entry.referenceType === "purchase") return "Amount Paid";
    return "Amount";
  })();

  const amountValue = (() => {
    if (entry.referenceType === "payment-in") return entry.receivedAmount || 0;
    if (entry.referenceType === "payment-out") return entry.paidAmount || 0;
    if (entry.referenceType === "sale") return entry.receivedAmount || 0;
    if (entry.referenceType === "purchase") return entry.paidAmount || 0;
    if (entry.referenceType === "expense") return entry.totalAmount || 0;
    return 0;
  })();

  const handleSettlement = async () => {
    if (entry.dueAmount == 0) return toast.info("Already settled");
    if (settleAmount > entry.dueAmount)
      return toast.error("Settle amount must be less than due amount");
    if (settleAmount > entry?.party?.credit_balance)
      return toast.error(
        "Settle amount must be less than or equal party credit balance",
      );

    const payload = {
      partyId: entry?.party?._id,
      amount: Number(settleAmount),
      referenceId: entry?.referenceId,
      referenceType: entry?.referenceType,
    };
    try {
      const res = await makeSettlement(payload);
      if (res.data.status) {
        toast.success(res.data.message);

        setEntry(res.data.response);
        setIsSettelement(false);
        setSettleAmount("");
        if (typeof onSettlementSuccess === "function") {
          onSettlementSuccess(res.data.response.party);
        }
        return;
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      console.log("handleSettlement", error);
      toast.error(
        error.response?.data?.message ||
          "Can't settle amount, please try again",
      );
    }
  };

  const handleNavigate = (id, type) => {
    if (!id) return;

    if (["purchase"].includes(type)) {
      navigate(`/purchases/create/${id}`);
    } else if (type === "purchase-order") {
      navigate(`/purchases/order/${id}`);
    } else if (["sale"].includes(type)) {
      navigate(`/sales/create/${id}`);
    } else if (["sale-order"].includes(type)) {
      navigate(`/sales/order/${id}`);
    } else {
      console.error("Unknown reference type:", type);
    }
  };

  const handleDelete = async (id, name, referenceType) => {
    const isConfirm = await confirmDelete(`${name} (${referenceType})`);
    if (!isConfirm) return;

    if (!isConfirm) return;

    try {
      let response;

      if (entry.referenceType === "purchase") {
        response = await deletePurchase(id);
      } else if (entry.referenceType === "purchaseReturn") {
        response = await deletePurchaseReturn(id);
      } else if (entry.referenceType === "sale") {
        response = await deleteSale(id);
      } else if (entry.referenceType === "saleReturn") {
        response = await deleteSaleReturn(id);
      } else if (entry.referenceType === "payment-in") {
        response = await deletePaymentIn(id);
      } else if (entry.referenceType === "payment-out") {
        response = await deletePaymentOut(id);
      } else if (entry.referenceType === "purchase-order") {
        response = await deletePurchaseOrder(id);
      } else if (entry.referenceType === "sale-order") {
        response = await deleteSaleOrder(id);
      } else if (entry.referenceType === "expense") {
        response = await deleteExpense(id);
      } else {
        throw new Error("Invalid reference type");
      }
      toastSuccess(response.data.message || "Deleted");

      onClose();

      if (typeof onDeleteSuccess === "function") {
        onDeleteSuccess();
      }
    } catch (error) {
      console.error(error);
      toastError(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Something went wrong",
      );
    }
  };

  return (
    <div className="position-fixed top-0 end-0 h-100 bg-white overflow-hidden p-0 width_style">
      <div className="card h-100 rounded-0 mb-1">
        {/* Card header */}
        <div className="text-white p-3 d-flex bg-custom align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <i
              className="bi bi-arrow-left"
              style={{
                cursor: "pointer",
                fontSize: "1.2rem",
                paddingTop: "4px",
              }}
              onClick={onClose}
            ></i>
            <p className="mb-0">
              {entry.referenceType === "purchase"
                ? "Purchase"
                : entry.referenceType === "purchase-order"
                  ? "Purchase Order"
                  : entry.referenceType === "sale"
                    ? "Sale"
                    : entry.referenceType === "sale-order"
                      ? "Sale Order"
                      : entry.referenceType === "purchaseReturn"
                        ? "Purchase Return"
                        : entry.referenceType === "saleReturn"
                          ? "Sale Return"
                          : entry.referenceType === "payment-in"
                            ? "Payment Received"
                            : entry.referenceType === "payment-out"
                              ? "Payment Paid"
                              : entry.referenceType === "expense"
                                ? "Expense"
                                : "Payment Made"}
            </p>
          </div>
          <div className="d-flex flex-column align-items-end">
            <small className="text-white">
              {moment(entry.date).format("DD MMM, YYYY")}
            </small>
          </div>
        </div>
        <div className="flex-grow-1 overflow-y-auto pt-0 p-3">
          <div className=" py-2 px-0">
            <p className="font-semibold text-end mb-2">
              {invoiceNumber && `#${invoiceNumber}`}
            </p>
            {/* <div className="row mb-2">
              <div className="col-md-12 text-md-end">
                {entry.referenceType !== "expense" && (
                  <p>
                    Party Balance:
                    <span
                      className={
                        entry.balance === 0
                          ? "text-muted"
                          : entry.balance < 0
                          ? "text-danger fw-semibold"
                          : "text-success fw-semibold"
                      }
                    >
                      {" "}
                      ₹ {Math.abs(entry.balance).toFixed(2)}
                    </span>
                  </p>
                )}
              </div>
            </div> */}

            {entry.referenceType !== "expense" && (
              <h6 className="fw-bold " style={{ color: "#0B2E69" }}>
                {entry.referenceType === "purchase" ||
                entry.referenceType === "payment-out"
                  ? "Vendor Info"
                  : "Customer Info"}
              </h6>
            )}

            <div className="card shadow-sm">
              <div className="p-3" style={{ fontSize: "13px" }}>
                <p className="mb-1">
                  <span className="fw-medium">
                    {entry.referenceType === "sale" ||
                    entry.referenceType === "saleReturn"
                      ? "Customer Name : "
                      : entry.referenceType === "expense"
                        ? "Expense : "
                        : "Party Name : "}
                  </span>

                  <span>
                    {entry.referenceType === "expense"
                      ? entry?.expenseTitle
                      : entry.party?.name || entry?.partyName || ""}
                  </span>
                </p>

                {entry.party?.mobile && (
                  <p className="mb-0">
                    <span className="fw-medium">Phone Number :</span>{" "}
                    <span>{entry.party?.mobile || ""}</span>{" "}
                  </p>
                )}

                {entry.referenceType !== "expense" &&
                  entry.party?.credit_balance !== 0 && (
                    <p className="mb-0 mt-1">
                      <span className="fw-medium">
                        {entry.party?.partyType === "vendor"
                          ? "Vendor Credit Balance"
                          : "Customer Credit Balance"}
                        :
                      </span>{" "}
                      <span
                        className={
                          entry.party?.credit_balance > 0
                            ? "text-success"
                            : "text-danger"
                        }
                      >
                        ₹ {Math.abs(entry.party?.credit_balance).toFixed(2)}
                        {entry.party?.credit_balance > 0
                          ? " (Advance)"
                          : " (Due)"}
                      </span>
                    </p>
                  )}
              </div>
            </div>

            <div>
              {/* ITEMS DISPLAY */}
              {entry.items?.length > 0 && (
                <div className="mt-2">
                  <h6 className="fw-bold mb-2 " style={{ color: "#0B2E69" }}>
                    Items & Products
                  </h6>

                  {entry.items.map((item, index) => {
                    const qty = parseFloat(
                      item.quantity || item.sellingQty || 0,
                    );

                    let price = 0;
                    if (
                      entry.referenceType === "purchase" ||
                      entry.referenceType === "purchaseReturn" ||
                      entry.referenceType === "purchase-order"
                    ) {
                      price = parseFloat(
                        item.purchasePrice || item.baseCost || 0,
                      );
                    } else {
                      price = parseFloat(
                        item.sellingPrice ||
                          item.salePrice ||
                          item.baseCost ||
                          0,
                      );
                    }

                    const baseSubtotal = qty * price;

                    const gstRate = parseFloat(item.gstRate || 0);
                    const taxType = item.taxType || "exclusive";
                    const isInclusive = taxType === "inclusive";

                    let gstAmount = 0;
                    if (gstRate > 0) {
                      gstAmount = isInclusive
                        ? (baseSubtotal * gstRate) / (100 + gstRate)
                        : (baseSubtotal * gstRate) / 100;
                    }

                    return (
                      <div
                        key={index}
                        className="border p-3 mb-3 rounded shadow-sm bg-light"
                      >
                        <p className="fw-bold mb-2">
                          {index + 1}. {item.itemName}
                        </p>

                        {/* Qty × Price */}
                        <div
                          className="d-flex justify-content-between text-muted"
                          style={{ fontSize: "13.5px" }}
                        >
                          <span>Quantity × Price</span>
                          <span>
                            {qty} × ₹ {price.toFixed(2)} = ₹{" "}
                            {baseSubtotal.toFixed(2)}
                          </span>
                        </div>

                        {/* GST BLOCK */}
                        {gstRate > 0 && (
                          <div
                            className="d-flex justify-content-between text-muted mt-1"
                            style={{ fontSize: "13.5px" }}
                          >
                            <span>
                              GST ({gstRate}%) –{" "}
                              {isInclusive ? "Inclusive" : "Exclusive"}
                            </span>
                            <span>₹ {gstAmount.toFixed(2)}</span>
                          </div>
                        )}

                        {/* Total */}
                        <div className="d-flex justify-content-between mt-2 border-top pt-2">
                          <span>Total</span>
                          <span>
                            ₹{" "}
                            {isInclusive
                              ? baseSubtotal.toFixed(2)
                              : (baseSubtotal + gstAmount).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {entry.services?.length > 0 && (
                <div className="">
                  <h6 className="fw-bold mb-1 " style={{ color: "#0B2E69" }}>
                    Services
                  </h6>
                  {entry.services.map((service, i) => {
                    const qty = parseFloat(
                      service.qty || service.quantity || 1,
                    );
                    const unitPrice = parseFloat(service.baseCost) || 0;
                    const baseAmount = qty * unitPrice;

                    const gstRate = parseFloat(service.gstRate) || 0;
                    const taxType = service.taxType || "exclusive";
                    const isInclusive = taxType === "inclusive";

                    let gstAmount = 0;
                    if (isInclusive && gstRate > 0) {
                      gstAmount = (baseAmount * gstRate) / (100 + gstRate);
                    } else if (!isInclusive && gstRate > 0) {
                      gstAmount = (baseAmount * gstRate) / 100;
                    }

                    const total = isInclusive
                      ? baseAmount
                      : baseAmount + gstAmount;

                    return (
                      <div
                        key={service.serviceId || i}
                        className="border p-3 mb-3 rounded shadow-sm bg-light"
                      >
                        <p className="mb-2 fw-bold">
                          {i + 1}. {service.name}
                        </p>

                        {/* Qty × Price */}
                        <div
                          className="d-flex justify-content-between text-muted"
                          style={{ fontSize: "13.5px" }}
                        >
                          <span>Quantity × Price</span>
                          <span>
                            {qty} × ₹ {unitPrice.toFixed(2)} = ₹{" "}
                            {baseAmount.toFixed(2)}
                          </span>
                        </div>

                        {/* Taxable Amount */}
                        {/* {gstRate > 0 && (
                          <div
                            className="d-flex justify-content-between text-muted my-1"
                            style={{ fontSize: "13.5px" }}
                          >
                            <span>Taxable Amount</span>
                            <span>
                              {taxType === "exclusive"
                                ? `₹${baseAmount.toFixed(2)}`
                                : `₹${(baseAmount - gstAmount).toFixed(2)}`}
                            </span>
                          </div>
                        )} */}

                        {/* GST */}
                        {gstRate > 0 && (
                          <div
                            className="d-flex justify-content-between text-muted"
                            style={{ fontSize: "13.5px" }}
                          >
                            <span>
                              GST ({gstRate}%) -{" "}
                              {isInclusive ? "Inclusive" : "Exclusive"}
                            </span>
                            <span className="">₹ {gstAmount.toFixed(2)}</span>
                          </div>
                        )}

                        {/* Total */}
                        <div
                          className="d-flex justify-content-between mt-2 pt-2 border-top"
                          style={{ fontSize: "14px" }}
                        >
                          <span>Total</span>
                          <span>₹ {total.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}

                  {/* Total GST from Services */}
                  {/* {(() => {
                    const totalGST = entry.services.reduce(
                      (sum, s) => sum + (parseFloat(s.gstAmount) || 0),
                      0
                    );
                    return totalGST > 0 ? (
                      <div className="text-end mt-2 fw-bold text-success">
                        Total GST: ₹ {totalGST.toFixed(2)}
                      </div>
                    ) : null;
                  })()} */}
                </div>
              )}

              {entry.otherExpenses?.length > 0 && (
                <div className="">
                  <h6 className="fw-bold" style={{ color: "#0B2E69" }}>
                    Other Expenses
                  </h6>
                  {entry.otherExpenses.map((exp, i) => {
                    const price = parseFloat(exp.price) || 0;
                    const gstRate = parseFloat(exp.gstRate) || 0;
                    const taxType = exp.taxType || "exclusive";
                    const isInclusive = taxType === "inclusive";

                    let gstAmount = 0;
                    if (isInclusive && gstRate > 0) {
                      gstAmount = (price * gstRate) / (100 + gstRate);
                    } else if (!isInclusive && gstRate > 0) {
                      gstAmount = (price * gstRate) / 100;
                    }

                    const total = isInclusive ? price : price + gstAmount;

                    return (
                      <div
                        key={exp._id || i}
                        className="border p-3 mb-3 rounded shadow-sm bg-light"
                      >
                        <p className="mb-2 fw-bold">
                          {i + 1}. {exp.name}
                        </p>

                        <div
                          className="d-flex justify-content-between text-muted"
                          style={{ fontSize: "13.5px" }}
                        >
                          <span>Price</span>
                          <span>₹ {price.toFixed(2)}</span>
                        </div>

                        {/* {gstRate > 0 && (
                          <div
                            className="d-flex justify-content-between text-muted my-1"
                            style={{ fontSize: "13.5px" }}
                          >
                            <span>Taxable Amount</span>
                            <span>
                              {taxType == "exclusive"
                                ? `₹ ${price}`
                                : `₹ ${(price - gstAmount).toFixed(2)}`}
                            </span>
                          </div>
                        )} */}

                        {gstRate > 0 && (
                          <div
                            className="d-flex justify-content-between text-muted"
                            style={{ fontSize: "13.5px" }}
                          >
                            <span>
                              GST ({gstRate}%) -{" "}
                              {isInclusive ? "Inclusive" : "Exclusive"}
                            </span>
                            <span className="">₹ {gstAmount.toFixed(2)}</span>
                          </div>
                        )}
                        <div
                          className="d-flex justify-content-between mt-2 pt-2 border-top"
                          style={{ fontSize: "14px" }}
                        >
                          <span>Total</span>
                          <span>₹ {total.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })}

                  {/* <div className="text-end mt-2 fw-bold text-success">
                    Total GST from Expenses: ₹
                    {entry.otherExpenses
                      .reduce((s, e) => {
                        const p = parseFloat(e.price) || 0;
                        const r = parseFloat(e.gstRate) || 0;
                        const t = e.taxType || "exclusive";
                        if (t === "inclusive" && r > 0)
                          return s + (p * r) / (100 + r);
                        if (t === "exclusive" && r > 0)
                          return s + (p * r) / 100;
                        return s;
                      }, 0)
                      .toFixed(2)}
                  </div> */}
                </div>
              )}
            </div>

            {/* Payment In  */}
            {entry.referenceType === "payment-in" &&
              entry.againstType === "sale" &&
              entry.against_sale && (
                <div className="card shadow-sm bg-light">
                  <div className="p-3" style={{ fontSize: "14px" }}>
                    <div className="d-flex justify-content-between mb-1">
                      <strong>Payment Against Sale</strong>
                      <span className="text-muted">
                        Invoice #{entry.against_sale.invoiceNumber}
                      </span>
                    </div>

                    {/* <div className="d-flex justify-content-between">
                      <span>Total Sale Amount</span>
                      <strong>₹{entry.against_sale.totalAmount}</strong>
                    </div> */}

                    <div className="d-flex justify-content-between mt-3">
                      <span>Amount</span>
                      <strong>₹{entry.against_sale.appliedAmount}</strong>
                    </div>
                  </div>
                </div>
              )}

            {(entry.referenceType === "saleReturn" ||
              entry.referenceType === "purchaseReturn") &&
              entry.reason && (
                <div className="form-group mb-3">
                  <label htmlFor="reason">Reason for Return</label>
                  <input
                    type="text"
                    id="reason"
                    className="form-control"
                    value={entry.reason}
                    readOnly
                  />
                </div>
              )}
            {/* Return Item */}
            {entry.returns?.length > 0 && (
              <div className="mt-3">
                <h6 className="fw-bold text-danger">Returned Items</h6>

                {entry.returns.map((ret, index) => {
                  const item = ret.items?.[0];

                  const qty = parseFloat(item?.quantity || ret.quantity || 0);

                  const price =
                    parseFloat(item?.sellingPrice) ||
                    parseFloat(item?.purchasePrice) ||
                    0;

                  const total =
                    parseFloat(item?.totalReturnAmount) ||
                    parseFloat(ret.returnItemAmount) ||
                    qty * price;

                  return (
                    <div
                      key={ret._id}
                      className="border p-3 mb-2 rounded bg-light"
                    >
                      {/* Item Name */}
                      <p className="mb-1 fw-semibold">
                        {index + 1}.{" "}
                        {item?.itemName || ret.product?.name || "-"}
                      </p>

                      {/* Quantity × Price */}
                      <div className="d-flex justify-content-between text-muted small">
                        <span>Quantity × Price</span>
                        <span>
                          {qty} × ₹ {price.toFixed(2)} = ₹ {total.toFixed(2)}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between text-muted small">
                        <span>
                          GST
                          {/* ({item?.gstRate || 0}%) */}
                        </span>
                        <span>
                          ₹{" "}
                          {Number(
                            item?.gstAmount || ret.gstAmount || 0,
                          ).toFixed(2)}
                        </span>
                      </div>

                      {/* Reason */}
                      {ret.reason && (
                        <div className="mt-1 text-muted small">
                          <strong>Reason:</strong> {ret.reason}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            <div className="p-3 bg-light rounded border shadow-sm">
              {(() => {
                // 1. ITEMS – recalculate with proper inclusive/exclusive GST
                const itemsResult = (entry.items || []).reduce(
                  (acc, item) => {
                    const qty = parseFloat(
                      item.quantity || item.sellingQty || 0,
                    );

                    // Choose correct price field based on transaction type
                    let price = 0;
                    if (
                      entry.referenceType === "purchase" ||
                      entry.referenceType === "purchaseReturn" ||
                      entry.referenceType === "purchase-order"
                    ) {
                      price = parseFloat(
                        item.purchasePrice || item.baseCost || 0,
                      );
                    } else {
                      // sale, saleReturn, etc.
                      price = parseFloat(
                        item.sellingPrice ||
                          item.salePrice ||
                          item.baseCost ||
                          0,
                      );
                    }

                    const baseSubtotal = qty * price;
                    const gstRate = parseFloat(item.gstRate || 0);
                    const taxType = item.taxType || "exclusive";
                    const isInclusive = taxType === "inclusive";

                    let gstAmount = 0;
                    if (gstRate > 0) {
                      if (isInclusive) {
                        gstAmount = (baseSubtotal * gstRate) / (100 + gstRate);
                      } else {
                        gstAmount = (baseSubtotal * gstRate) / 100;
                      }
                    }

                    const taxable = isInclusive
                      ? baseSubtotal - gstAmount
                      : baseSubtotal;
                    acc.taxable += taxable;
                    acc.gst += gstAmount;
                    acc.total += baseSubtotal + (isInclusive ? 0 : gstAmount);

                    return acc;
                  },
                  { taxable: 0, gst: 0, total: 0 },
                );

                // 2. SERVICES – already saved correctly
                const servicesResult = (entry.services || []).reduce(
                  (acc, s) => {
                    const qty = Number(s.qty || 1);
                    const unit = Number(s.baseCost || 0);
                    const base = qty * unit;

                    const gstRate = Number(s.gstRate || 0);
                    const taxType = s.taxType || "exclusive";
                    const isInclusive = taxType === "inclusive";

                    let gst = 0;

                    if (gstRate > 0) {
                      if (isInclusive) {
                        gst = (base * gstRate) / (100 + gstRate);
                      } else {
                        gst = (base * gstRate) / 100;
                      }
                    }

                    const taxable = isInclusive ? base - gst : base;
                    const total = isInclusive ? base : base + gst;

                    acc.taxable += taxable;
                    acc.gst += gst;
                    acc.total += total;

                    return acc;
                  },
                  { taxable: 0, gst: 0, total: 0 },
                );

                // 3. OTHER EXPENSES – also support GST
                const expensesResult = (entry.otherExpenses || []).reduce(
                  (acc, e) => {
                    const price = parseFloat(e.price || 0);
                    const gstRate = parseFloat(e.gstRate || 0);
                    const taxType = e.taxType || "exclusive";
                    const isInclusive = taxType === "inclusive";

                    let gst = 0;
                    if (gstRate > 0) {
                      if (isInclusive) {
                        gst = (price * gstRate) / (100 + gstRate);
                      } else {
                        gst = (price * gstRate) / 100;
                      }
                    }

                    acc.taxable += isInclusive ? price - gst : price;
                    acc.gst += gst;
                    // acc.total += price;
                    acc.total += isInclusive ? price : price + gst;

                    return acc;
                  },
                  { taxable: 0, gst: 0, total: 0 },
                );

                // FINAL TOTALS
                const totalTaxable =
                  itemsResult.taxable +
                  servicesResult.taxable +
                  expensesResult.taxable;

                const totalGST =
                  itemsResult.gst + servicesResult.gst + expensesResult.gst;

                const received = Math.abs(entry.receivedAmount || 0);
                const due = entry.dueAmount;
                const returnAmount = entry.returns?.reduce((sum, ret) => {
                  const item = ret.items?.[0];

                  const qty = Number(item?.quantity || 0);
                  const price = Number(
                    item?.purchasePrice || item?.sellingPrice || 0,
                  );

                  // FIXED
                  const amount =
                    (item?.totalReturnAmount &&
                      Number(item.totalReturnAmount)) ||
                    (ret?.totalReturnAmount && Number(ret.totalReturnAmount)) ||
                    qty * price + Number(item?.gstAmount || 0);

                  return sum + Number(amount);
                }, 0);
                const hasReturn = entry.returns && entry.returns.length > 0;
                const finalAmount =
                  (entry.totalBeforeDiscount || 0) -
                  (entry.discountAmount || 0) -
                  (returnAmount || 0);
                return (
                  <>
                    {/* Taxable Amount */}
                    {totalTaxable > 0 && (
                      <div className="d-flex justify-content-between mb-1">
                        <span className="text-muted small">
                          Total Amount (Without Tax)
                        </span>
                        <span className="fw-medium small">
                          ₹ {totalTaxable.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {/* Total GST */}
                    {totalGST > 0 && (
                      <div className="d-flex justify-content-between pb-2 mb-2 border-bottom">
                        <span className="text-muted small">Total GST</span>
                        <span className="fw-medium small">
                          ₹ {totalGST.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {/* Grand Total */}
                    {/* {!isPaymentEntry &&
                      entry.referenceType !== "saleReturn" &&
                      entry.referenceType !== "purchaseReturn" &&
                      entry.referenceType !== "sale-order" &&
                      entry.referenceType !== "purchase-order" && (
                        <div className="d-flex justify-content-between align-items-center pb-2">
                          <span className="fw-semibold">Grand Total</span>
                          <span className="fw-semibold">
                            ₹ {(entry.totalBeforeDiscount || 0).toFixed(2)}
                          </span>
                        </div>
                      )}{" "} */}
                    {/* Grand Total */}
                    {!isPaymentEntry &&
                      entry.referenceType !== "saleReturn" &&
                      entry.referenceType !== "purchaseReturn" &&
                      entry.referenceType !== "sale-order" &&
                      entry.referenceType !== "purchase-order" && (
                        <div className="d-flex justify-content-between align-items-center pb-2">
                          <span className="fw-semibold">Grand Total</span>
                          <span className="fw-semibold">
                            ₹ {(totalTaxable + totalGST).toFixed(2)}
                          </span>
                        </div>
                      )}
                    {/* Discount Amount */}
                    {Number(entry.discountAmount || 0) > 0 && (
                      <div className="d-flex justify-content-between mb-1 text-danger">
                        <span className="small">
                          Discount
                          {entry.discountType === "percent"
                            ? ` (${entry.discountValue}%)`
                            : ""}
                        </span>
                        <span className="small">
                          - ₹ {Number(entry.discountAmount).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {/* Return Amount */}
                    {!isPaymentEntry &&
                      entry.referenceType !== "saleReturn" &&
                      entry.referenceType !== "purchaseReturn" &&
                      returnAmount > 0 && (
                        <div className="d-flex justify-content-between text-warning mb-1">
                          <span className="small">Return Amount</span>
                          <span className="small">
                            - ₹ {returnAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                    {/* Final Amount (After Discount) */}
                    {entry.referenceType !== "sale-order" &&
                      entry.referenceType !== "purchase-order" && (
                        <>
                          {Number(entry.totalAmount || 0) > 0 &&
                            !isPaymentEntry && (
                              <div
                                className={`d-flex justify-content-between ${
                                  entry.referenceType !== "expense" &&
                                  entry.referenceType !== "saleReturn" &&
                                  entry.referenceType !== "purchaseReturn"
                                    ? "border-bottom pb-2 mb-2"
                                    : ""
                                }`}
                              >
                                <span className="fw-medium small">
                                  {entry.referenceType === "expense" ? (
                                    "Amount"
                                  ) : (
                                    <>
                                      Final Amount{" "}
                                      {!hasReturn &&
                                        Number(entry.discountAmount || 0) >
                                          0 && <span>(After Discount)</span>}
                                    </>
                                  )}
                                </span>
                                <span className="fw-medium small">
                                  ₹ {Number(finalAmount).toFixed(2)}
                                </span>
                              </div>
                            )}
                        </>
                      )}
                    {entry.referenceType !== "sale-order" &&
                      entry.referenceType !== "purchase-order" && (
                        <>
                          <div className="d-flex justify-content-between mt-1 text-success">
                            <span className="small">
                              {entry.referenceType === "expense"
                                ? "Amount Paid"
                                : amountText}
                            </span>
                            <span className="small">
                              ₹ {amountValue.toFixed(2)}
                            </span>
                          </div>
                        </>
                      )}
                    {/* Balance Due */}

                    {!isPaymentEntry &&
                      entry.referenceType !== "saleReturn" &&
                      entry.referenceType !== "purchaseReturn" && (
                        <div className="d-flex justify-content-between align-items-center mt-2 small">
                          <span className="">Balance Due</span>
                          <span
                            className={due > 0 ? "text-danger" : "text-success"}
                          >
                            ₹ {Math.abs(entry.dueAmount || 0).toFixed(2)}
                          </span>
                        </div>
                      )}
                  </>
                );
              })()}
            </div>
            {!isPaymentEntry && (
              <div
                className={`mt-3 ${isSettelement ? "border rounded p-3" : ""}`}
              >
                {isSettelement && (
                  <div className="mb-3">
                    <div className="d-flex  small fw-medium">
                      <span className="text-muted">
                        {entry.referenceType === "purchase"
                          ? "Vendor Credit Balance:"
                          : "Customer Credit Balance:"}
                      </span>
                      <span
                        className={`${
                          entry.party?.credit_balance >= 0
                            ? "text-success"
                            : "text-danger"
                        }`}
                      >
                        ₹{" "}
                        {Math.abs(entry.party?.credit_balance || 0).toFixed(2)}
                        {entry.party?.credit_balance < 0 && " (Overdue)"}
                      </span>
                    </div>
                  </div>
                )}

                <div className="d-flex gap-2 align-items-center justify-content-end flex-wrap">
                  {isSettelement && (
                    <div className="d-flex align-items-center gap-2 w-100">
                      {/* Amount Input */}
                      <input
                        className="form-control form-control-sm flex-grow-1"
                        placeholder="Enter amount..."
                        value={settleAmount}
                        onChange={(e) => setSettleAmount(e.target.value)}
                        style={{ minWidth: 0 }}
                      />

                      {/* Cancel Button */}
                      <button
                        className="btn btn-outline-danger btn-sm d-flex align-items-center justify-content-center"
                        style={{ width: "35px", height: "35px", flexShrink: 0 }}
                        onClick={() => {
                          setIsSettelement(false);
                          setSettleAmount(entry?.dueAmount || 0);
                        }}
                        title="Cancel Settlement"
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>

                      {/* Settle Button */}
                      <button
                        className="btn btn-success btn-sm text-nowrap py-2"
                        style={{ flexShrink: 0 }}
                        onClick={handleSettlement}
                      >
                        Settle
                      </button>
                    </div>
                  )}

                  {!isPaymentEntry &&
                    !isSettelement &&
                    entry.referenceType !== "saleReturn" &&
                    entry.referenceType !== "purchaseReturn" &&
                    entry.referenceType !== "sale-order" &&
                    entry.referenceType !== "purchase-order" &&
                    entry?.dueAmount > 0 && (
                      <button
                        className="btn btn-secondary btn-sm px-3 text-nowrap"
                        onClick={() => setIsSettelement(true)}
                      >
                        Make Settlement
                      </button>
                    )}
                </div>
              </div>
            )}
          </div>
        </div>
        {showActions && (
          <div
            className="card-footer bg-white border-top d-flex mt-auto align-items-center"
            style={{ position: "sticky", bottom: "0" }}
          >
            {/* LEFT SIDE */}
            <div>
              {!isPaymentEntry && !isConvertedEdit && (
                <button
                  className="btn btn-outline-primary btn-sm py-1 px-2"
                  onClick={() =>
                    handleNavigate(entry.referenceId, entry.referenceType)
                  }
                >
                  Edit
                </button>
              )}
            </div>

            {/* RIGHT SIDE */}
            <div className="ms-auto">
              {!isPaymentEntry &&
                entry.referenceType !== "saleReturn" &&
                entry.referenceType !== "purchaseReturn" && (
                  <button
                    className="btn btn-outline-danger btn-sm py-1 px-2"
                    onClick={() =>
                      handleDelete(
                        entry.referenceId,
                        entry.party?.name,
                        entry.referenceType,
                      )
                    }
                  >
                    Delete
                  </button>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PartyCardDetail;
